import { useEffect, useState, useSyncExternalStore } from "react";
import {
  TRADING_ENV,
  TradingCommands,
  accountIds,
  gateAction,
  isTradableEnvironment,
  nextGateChangeMs,
  sameGateContext,
  unconfirmedAccountIds,
  useConnection,
  useTrading,
  type Gate,
  type GateAction,
  type GateContext,
  type RiskGateInput,
  type RiskState,
  type TradingState,
  type CommandSender,
} from "@helm/core";
import { tradingSender } from "../session";
import { useSlice } from "../viewmodel/slice";

/**
 * The window's trading controls (owner decision 2026-09-27, ADR-0021): one `TradingCommands`
 * (one send per click, never a retry, busy keys for double-click suppression) and the gate
 * context every control reads. See `@helm/core` trading/gate.ts for the rules.
 */
export const commands = new TradingCommands(() => logged(tradingSender()));

/**
 * The last trading commands this window sent (case + order id; no prices, no account), for the
 * browser e2e checks — e.g. "a chart drag sent exactly one ModifyOrder". `window.__helmTradeLog()`.
 */
const sentLog: { case: string; id: string }[] = [];
const wrapped = new WeakMap<CommandSender, CommandSender>();

function logged(s: CommandSender | null): CommandSender | null {
  if (s === null) return null;
  let w = wrapped.get(s);
  if (!w) {
    w = {
      send: (body) => {
        const v = body.value as { clientOrderId?: unknown } | undefined;
        sentLog.push({
          case: body.case ?? "",
          id: typeof v?.clientOrderId === "string" ? v.clientOrderId : "",
        });
        if (sentLog.length > 200) sentLog.shift();
        return s.send(body);
      },
      subscribe: (l) => s.subscribe(l),
    };
    wrapped.set(s, w);
  }
  return w;
}

(window as unknown as { __helmTradeLog?: () => { case: string; id: string }[] }).__helmTradeLog = () => [
  ...sentLog,
];

/** Is the control `key` waiting for its CommandResult? */
export function useBusy(key: string): boolean {
  return useSyncExternalStore(
    (l) => commands.subscribe(l),
    () => commands.isBusy(key),
  );
}

/**
 * `RiskState` → the gate's view (ADR-0020 fields). `blocks_new_orders` is the gateway engine's own
 * verdict; `config_error` names an invalid risk config (fail closed). Status and lockout are kept
 * too, so the gate still blocks on them if a gateway predating ADR-0020 sends only those (proto3
 * then reads the new fields as false / "").
 */
export function riskGateInput(r: RiskState): RiskGateInput {
  const out: RiskGateInput = {
    status: r.status,
    lockoutUntilMs: r.lockoutUntilMs,
    blocksNewOrders: r.blocksNewOrders,
  };
  if (r.configError !== "") out.configError = r.configError;
  return out;
}

/** Pure: the gate context from the connection flag, the trading state and a clock. */
export function gateContextOf(connected: boolean, s: TradingState, nowMs: number): GateContext {
  const accounts = new Map<string, number>();
  for (const id of accountIds(s)) {
    accounts.set(id, s.accounts.get(id)?.environment ?? TRADING_ENV.UNSPECIFIED);
  }
  const risk = new Map<string, RiskGateInput>();
  for (const [id, r] of s.risk) risk.set(id, riskGateInput(r));
  const session = s.session
    ? {
        status: s.session.status,
        environment: s.session.environment,
        detail: s.session.detail,
        tradingEnabled: s.session.tradingEnabled,
      }
    : null;
  // ADR-0032: an account whose positions Rithmic has not confirmed (the gateway's
  // POSITIONS_UNCONFIRMED notice — also for an account HELM shows FLAT — or a position flagged
  // `mismatch`); exits on it are refused (the gateway refuses them too).
  const unconfirmedAccounts = new Set(unconfirmedAccountIds(s));
  return { gatewayConnected: connected, session, accounts, risk, nowMs, unconfirmedAccounts };
}

/**
 * The gate context, re-evaluated on every trading frame and once a second while a lockout runs.
 * It keeps its identity (and its reader does not render) while every gate would answer the same:
 * RiskState frames arrive with each quote while a position is open, mostly with P&L only.
 */
export function useGateContext(): GateContext {
  const connected = useConnection((c) => c.status === "connected");
  const [now, setNow] = useState(() => Date.now());
  const ctx = useSlice(
    useTrading.subscribe,
    useTrading.getState,
    (s) => gateContextOf(connected, s, now),
    sameGateContext,
  );
  const next = nextGateChangeMs(ctx);
  useEffect(() => {
    if (next === null) return;
    // Countdown text and the automatic unblock at the lockout's end.
    const t = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(t);
    };
  }, [next]);
  return ctx;
}

export function useGate(action: GateAction): Gate {
  return gateAction(useGateContext(), action);
}

/** The session's environment when new orders can go to it (MOCK / TEST, or PAPER / LIVE with the
 * login's switch on, ADR-0027), else null. */
export function useTradableEnvironment(): number | null {
  const env = useTrading((t) => t.session?.environment ?? null);
  const on = useTrading((t) => t.session?.tradingEnabled === true);
  return isTradableEnvironment(env, on) ? env : null;
}
